import React from 'react';
import { Modal } from 'antd';
import { CHANGELOG } from '../changelog';

interface Props {
  open: boolean;
  sinceVersion: string;
  onClose: () => void;
}

// ── Shared styles (matches UserGuideModal palette) ───────────────────────────
const S = {
  h2: { margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#e2e8f0', letterSpacing: 0.3 } as React.CSSProperties,
  date: { margin: '0 0 12px', fontSize: 11, color: '#64748b', textTransform: 'uppercase' as const, letterSpacing: 1 },
  sectionLabel: { margin: '10px 0 4px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 1 } as React.CSSProperties,
  ul: { margin: '0 0 4px', paddingLeft: 18, fontSize: 12, lineHeight: 1.8 } as React.CSSProperties,
  li: { marginBottom: 2 } as React.CSSProperties,
  divider: { borderTop: '1px solid #2a2a40', margin: '16px 0' } as React.CSSProperties,
};

/** Semver comparison — returns true if `a` is strictly greater than `b`. */
function semverGt(a: string, b: string): boolean {
  const parse = (s: string) => s.split('.').map(Number);
  const [aMaj, aMin, aPat] = parse(a);
  const [bMaj, bMin, bPat] = parse(b);
  if (aMaj !== bMaj) return aMaj > bMaj;
  if (aMin !== bMin) return aMin > bMin;
  return aPat > bPat;
}

const WhatsNewModal: React.FC<Props> = ({ open, sinceVersion, onClose }) => {
  // Show all changelog entries strictly newer than sinceVersion.
  // If sinceVersion is '' (first time ever), show only the latest entry.
  const entries = sinceVersion === ''
    ? CHANGELOG.slice(0, 1)
    : CHANGELOG.filter((e) => semverGt(e.version, sinceVersion));

  if (entries.length === 0) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ textAlign: 'right' }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 18px', borderRadius: 6, border: 'none',
              background: '#3b82f6', color: '#fff', fontWeight: 600,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      }
      width={520}
      title={null}
      styles={{
        body: { background: '#12121f', borderRadius: 10, padding: 0 },
        header: { display: 'none' },
      }}
    >
      {/* Header band */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a32 0%, #0d0d1a 100%)',
        borderRadius: '10px 10px 0 0',
        padding: '24px 28px 18px',
        borderBottom: '1px solid #2a2a40',
      }}>
        <div style={{ fontSize: 11, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
          What's New
        </div>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>
          StoryBall {entries[0].version}
        </h1>
      </div>

      {/* Entries */}
      <div style={{ padding: '20px 28px 8px', maxHeight: 420, overflowY: 'auto' }}>
        {entries.map((entry, idx) => (
          <div key={entry.version}>
            {idx > 0 && <div style={S.divider} />}
            <h2 style={S.h2}>v{entry.version}</h2>
            <p style={S.date}>{entry.date}</p>
            {entry.features && entry.features.length > 0 && (
              <>
                <p style={{ ...S.sectionLabel, color: '#3b82f6' }}>New Features</p>
                <ul style={{ ...S.ul, color: '#94a3b8' }}>
                  {entry.features.map((item, i) => (
                    <li key={i} style={S.li}>{item}</li>
                  ))}
                </ul>
              </>
            )}
            {entry.fixes && entry.fixes.length > 0 && (
              <>
                <p style={{ ...S.sectionLabel, color: '#22c55e' }}>Bug Fixes</p>
                <ul style={{ ...S.ul, color: '#94a3b8' }}>
                  {entry.fixes.map((item, i) => (
                    <li key={i} style={S.li}>{item}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default WhatsNewModal;
